import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { uploadFileResumable } from "@/services/storage";
import { resolveImg } from "@/services/cmsApi";
import { SPECS, checkImage, fmtKB, HARD_MAX_BYTES } from "@/lib/imageSpecs";
import { cn } from "@/lib/utils";

const readSize = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const im = new Image();
  im.onload = () => { resolve({ width: im.naturalWidth, height: im.naturalHeight }); URL.revokeObjectURL(url); };
  im.onerror = () => { resolve({ width: 0, height: 0 }); URL.revokeObjectURL(url); };
  im.src = url;
});
const safeName = (n) => String(n || "image").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").slice(-80);

// Image slot used everywhere in Customer CMS: paste a URL OR upload a file
// (button or drag-and-drop), with the recommended size shown and the picked file checked.
// spec: from lib/imageSpecs (defaults to a general image). folder: Storage folder.
export function ImageField({ value, onChange, testId, spec = SPECS.generic, folder = "app_content/site", aspect, label }) {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [broken, setBroken] = useState(false);
  const [natural, setNatural] = useState(null);
  const [picked, setPicked] = useState(null); // { width, height, bytes, name, problems }
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) return toast.error("That isn't an image file. Use JPG, PNG, WebP or SVG.");
    if (file.size > HARD_MAX_BYTES) return toast.error(`This file is ${fmtKB(file.size)}. The limit is 5 MB — please compress it first.`);
    const dims = file.type === "image/svg+xml" ? { width: 0, height: 0 } : await readSize(file);
    const problems = checkImage(spec, { ...dims, bytes: file.size });
    setPicked({ ...dims, bytes: file.size, name: file.name, problems });
    setBusy(true); setPct(0);
    try {
      const url = await uploadFileResumable(`${folder}/${Date.now()}_${safeName(file.name)}`, file, setPct);
      onChange(url); setBroken(false);
      toast.success(problems.length ? "Uploaded — check the size notes, then Publish" : "Image uploaded — Publish to make it live");
    } catch (e) {
      toast.error(e?.code === "storage/unauthorized" ? "Upload refused by Firebase Storage. Publish the Storage rules in the Firebase console, then try again." : (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 max-w-xl" data-testid={testId}>
      {label && <p className="text-xs font-medium text-foreground">{label}</p>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => !busy && inputRef.current?.click()}
        title="Click or drop an image to upload"
        className={cn("relative rounded-lg border border-dashed bg-secondary/40 overflow-hidden flex items-center justify-center cursor-pointer transition-colors max-w-sm",
          drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}
        style={{ aspectRatio: aspect || `${spec.w} / ${spec.h}`, maxHeight: 240 }}
        data-testid={`${testId}-drop`}>
        {value && !broken
          ? <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })} onError={() => setBroken(true)} data-testid={`${testId}-preview`} />
          : <span className="text-xs text-muted-foreground flex flex-col items-center gap-1 p-3 text-center"><Icons.ImagePlus className="h-5 w-5" />{value ? "Preview unavailable" : "Click or drop an image here"}</span>}
        {busy && (
          <div className="absolute inset-0 bg-background/80 flex flex-col items-center justify-center gap-2">
            <span className="text-xs font-medium">Uploading… {pct}%</span>
            <div className="h-1.5 w-2/3 rounded-full bg-border overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Input value={value || ""} placeholder="Paste an image URL, or upload →" onChange={(e) => { setBroken(false); setNatural(null); setPicked(null); onChange(e.target.value); }} className="h-8 font-mono text-[11px]" data-testid={`${testId}-url`} />
        <Button type="button" size="sm" variant="outline" className="h-8 shrink-0 gap-1.5" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-upload-label`}>
          <Icons.Upload className="h-3.5 w-3.5" />{busy ? `${pct}%` : value ? "Replace" : "Upload"}
        </Button>
        {value && <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-muted-foreground" title="Remove image" onClick={() => { onChange(""); setNatural(null); setPicked(null); }} data-testid={`${testId}-clear`}><Icons.X className="h-3.5 w-3.5" /></Button>}
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml,image/avif,image/gif" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid={`${testId}-upload`} />
      </div>

      <div className="rounded-md bg-secondary/50 border border-border px-2.5 py-1.5 text-[11px] text-muted-foreground space-y-0.5" data-testid={`${testId}-spec`}>
        <p><span className="font-semibold text-foreground">Recommended: {spec.w} × {spec.h} px</span> · {spec.ratio} · {spec.formats} · under {spec.maxKB} KB (max 5 MB)</p>
        {spec.note && <p>{spec.note}</p>}
        {picked && (
          <p className={picked.problems.length ? "text-amber-700" : "text-emerald-700"} data-testid={`${testId}-check`}>
            {picked.problems.length ? <Icons.TriangleAlert className="inline h-3 w-3 mr-1 -mt-0.5" /> : <Icons.CheckCircle2 className="inline h-3 w-3 mr-1 -mt-0.5" />}
            Your image: {picked.width ? `${picked.width} × ${picked.height} px · ` : ""}{fmtKB(picked.bytes)}{picked.problems.length ? ` — ${picked.problems.join(" ")}` : " — good size."}
          </p>
        )}
        {!picked && natural && <p data-testid={`${testId}-current`}>Current image: {natural.w} × {natural.h} px</p>}
      </div>
    </div>
  );
}
